<template>
    <div>

      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Purchase History</h5>
          <p class="page-sub">Track and manage all stock purchase records</p>
        </div>
        <div class="page-actions">

          <!-- Date Range -->
          <div class="date-range">
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" placeholder="Start date" />
            </div>
            <span class="date-sep">—</span>
            <div class="date-field">
              <font-awesome-icon icon="calendar" class="date-icon" />
              <Datepicker v-model="tableData.end_date" :minDate="tableData.start_date" :enableTimePicker="false" class="date-picker" placeholder="End date" />
            </div>
            <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="search" class="me-1" />
              Filter
            </button>
          </div>

          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search purchases..." v-model="query" />
          </div>

          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>

          <!-- New Stock -->
          <router-link
            :to="$url('/make-purchase')"
            class="btn-primary-action"
            :class="permission.create_purchase != 1 ? 'disabled' : ''"
          >
            <font-awesome-icon icon="plus" /> New Stock
          </router-link>

        </div>
      </div>

      <!-- Data Card -->
      <div class="data-card">

        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading purchases...</p>
        </div>

        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="truck" />
          </div>
          <p class="state-title">No purchase records found</p>
          <p class="state-sub">Try adjusting the date range or record a new stock purchase.</p>
          <router-link v-if="permission.create_purchase == 1" :to="$url('/make-purchase')" class="btn-primary-action mt-2">
            <font-awesome-icon icon="plus" /> New Stock
          </router-link>
        </div>

        <!-- Table -->
        <template v-else>

          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> records
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
              </select>
            </div>
          </div>

          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Purchase No.</th>
                  <th>Supplier</th>
                  <th>Date & Time</th>
                  <th>Store</th>
                  <th>Total ({{ company?.currency?.symbol }})</th>
                  <th>Payment</th>
                  <th>Purchased By</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="purchase in details" :key="purchase.id">

                  <td>
                    <span class="batch-code">{{ purchase.batch_code }}</span>
                  </td>

                  <td>
                    <div v-if="purchase?.supplier?.supplier_name" class="user-cell">
                      <div class="user-avatar">{{ purchase.supplier.supplier_name.charAt(0) }}</div>
                      <span class="text-muted-cell">{{ purchase.supplier.supplier_name }}</span>
                    </div>
                    <span v-else class="text-muted-cell">—</span>
                  </td>

                  <td>
                    <p class="date-primary">{{ dateTime(purchase?.date) }}</p>
                    <p class="date-secondary">{{ timeConvert(purchase.time) }}</p>
                  </td>

                  <td>
                    <span class="store-tag">{{ purchase?.store?.store_name }}</span>
                  </td>

                  <td class="amount-value">
                    {{ $filters.formatMoney(purchase?.total_amount) }}
                  </td>

                  <td>
                    <span class="payment-badge" :class="paymentClass(purchase.payment_type)">
                      {{ paymentLabel(purchase.payment_type) }}
                    </span>
                  </td>

                  <td>
                    <div class="user-cell">
                      <div class="user-avatar user-avatar--sm">{{ purchase?.user?.fullname?.charAt(0) }}</div>
                      <span class="text-muted-cell">{{ purchase?.user?.fullname }}</span>
                    </div>
                  </td>

                  <td>
                    <span class="status-chip" :class="statusClass(purchase.status)">
                      {{ statusLabel(purchase.status) }}
                    </span>
                  </td>

                  <td class="col-actions">
                    <div class="action-btns">
                      <router-link
                        :to="permission.view_purchase == 1 ? '/purchase-receipt/' + purchase.id : '#'"
                        class="action-btn action-btn--view"
                        :class="permission.view_purchase != 1 ? 'disabled' : ''"
                        title="View"
                      >
                        <font-awesome-icon icon="eye" />
                      </router-link>
                      <!-- Approve / Reject for pending purchases -->
                      <template v-if="purchase.status === 'pending'">
                        <button
                          class="action-btn action-btn--approve"
                          :disabled="permission.create_purchase != 1 || actionLoading === purchase.id"
                          @click="handleApprove(purchase.id)"
                          title="Approve"
                        >
                          <font-awesome-icon v-if="actionLoading === purchase.id" icon="spinner" spin />
                          <font-awesome-icon v-else icon="check" />
                        </button>
                        <button
                          class="action-btn action-btn--reject"
                          :disabled="permission.create_purchase != 1 || actionLoading === purchase.id"
                          @click="handleReject(purchase.id)"
                          title="Reject"
                        >
                          <font-awesome-icon icon="times" />
                        </button>
                      </template>
                      <!-- Reverse only for approved purchases -->
                      <button
                        v-if="purchase.status === 'approved'"
                        class="action-btn action-btn--reverse"
                        data-bs-toggle="modal"
                        data-bs-target="#singleModal"
                        :class="permission.reverse_purchase != 1 ? 'disabled' : ''"
                        :disabled="permission.reverse_purchase != 1"
                        @click="getCurrentId(purchase.id)"
                        title="Reverse"
                      >
                        <font-awesome-icon icon="undo" />
                      </button>
                    </div>
                  </td>

                </tr>
              </tbody>
            </table>
          </div>

          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>

        </template>
      </div>

      <Alert
        :modalId="'singleModal'"
        @alertModal="handleAction()"
        :modalText="'Are you sure you want to reverse this purchase?'"
        :action="'alertModal'"
        title="Reverse Purchase"
      />

      <!-- ── Export Column Picker ── -->
      <transition name="fade">
        <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
          <div class="col-picker-modal">
            <div class="col-picker-header">
              <div>
                <h3 class="col-picker-title">Select Columns</h3>
                <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
              </div>
              <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
            </div>
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
              <span class="col-picker-sep">·</span>
              <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
              <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
                <span class="col-picker-label">{{ col.label }}</span>
              </label>
            </div>
            <div class="col-picker-footer">
              <button class="btn-outline" @click="showExportModal = false">Cancel</button>
              <button class="btn-primary-action" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
                <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
                Export {{ pendingExportFmt.toUpperCase() }}
              </button>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </template>

  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import Alert from '../../../components/AlertModal.vue';
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';

  export default {
    components: { Alert },
    mixins: [reusableMixin],

    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      return {
        print_content: [],
        headerArrayColumn: ['S/N','Purchase Number','Date','Time','Store','Total','Payment Type'],
        details: [], loading: false, query: '',
        selectedModel: [], allSelected: false,
        actionLoading: null,
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 5, search: '', column: 0,
          dir: 'desc', id: '', status: [0,1,2,3], query: '',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), currentID: null, store: useStore(), crudLoading: false,
        showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [], exportLoading: false,
        exportColumnDefs: [
          { key: 'batch_code',   label: 'Purchase No.', pdfWidth: 70 },
          { key: 'supplier',     label: 'Supplier',     pdfWidth: 100 },
          { key: 'date',         label: 'Date',         pdfWidth: 85 },
          { key: 'time',         label: 'Time',         pdfWidth: 65 },
          { key: 'store',        label: 'Store',        pdfWidth: 95 },
          { key: 'total_amount', label: 'Total',        pdfWidth: 85 },
          { key: 'payment_type', label: 'Payment',      pdfWidth: 80 },
          { key: 'purchased_by', label: 'Purchased By', pdfWidth: 105 },
          { key: 'status',       label: 'Status',       pdfWidth: 76 },
        ],
      };
    },

    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },

    watch: { query() { this.fetchData(); } },
    created() { this.fetchData(); this.getCompany(); },

    methods: {
      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
      },

      getPurchaseValue(row, key) {
        switch (key) {
          case 'batch_code':   return row.batch_code || '—';
          case 'supplier':     return row.supplier?.supplier_name || '—';
          case 'date':         return row.date || '—';
          case 'time':         return row.time || '—';
          case 'store':        return row.store?.store_name || '—';
          case 'total_amount': return Number(row.total_amount || 0).toFixed(2);
          case 'payment_type': return this.paymentLabel(row.payment_type);
          case 'purchased_by': return row.user?.fullname || '—';
          case 'status':       return this.statusLabel(row.status);
          default:             return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exportLoading   = true;
        this.showExportModal = false;
        try {
          const params = { ...this.tableData, page: 1, length: 10000 };
          const res    = await axios.get('/inventory/purchase', { params });
          const rows   = res.data.data.data;

          const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers = cols.map(c => c.label);
          const body    = rows.map(row => cols.map(c => this.getPurchaseValue(row, c.key)));
          const date    = new Date().toISOString().slice(0, 10);

          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body]
              .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
              .join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `purchases-${date}.csv`; a.click();
            URL.revokeObjectURL(url);
          } else {
            const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable    = 761;
            const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
            const scale     = usable / totalW;
            const colStyles = {};
            cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });

            doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
            doc.text('Purchase History', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);

            doc.autoTable({
              startY: 78, head: [headers], body,
              styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
              headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
              alternateRowStyles: { fillColor: [248, 250, 252] },
              columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
              didDrawPage(data) {
                doc.setFontSize(8); doc.setTextColor(192, 202, 214);
                doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
              },
            });
            doc.save(`purchases-${date}.pdf`);
          }
        } catch { this.toast.error('Export failed. Please try again.'); }
        finally  { this.exportLoading = false; }
      },

      getCurrentId(id) { this.currentID = id; },

      paymentLabel(type) {
        const map = { cash:'Cash', card:'Card', transfer:'Transfer', part:'Part Payment', split:'Split Payment', credit:'Credit' };
        return map[type] ?? (type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');
      },

      paymentClass(type) {
        const map = { cash:'payment--cash', card:'payment--card', transfer:'payment--transfer', part:'payment--part', split:'payment--split', credit:'payment--credit' };
        return map[type] ?? '';
      },

      statusLabel(status) {
        const map = { pending:'Pending Approval', approved:'Approved', rejected:'Rejected' };
        return map[status] ?? (status ? status.charAt(0).toUpperCase() + status.slice(1) : '—');
      },

      statusClass(status) {
        const map = { pending:'status--pending', approved:'status--approved', rejected:'status--rejected' };
        return map[status] ?? '';
      },

      dataToPrint(details) {
        this.print_content = details.map((purchase, idx) => ({
          sn: idx + 1, batch_code: purchase.batch_code, date: purchase.date,
          time: purchase.time, store: purchase.store?.store_name,
          total: purchase.total_amount, payment_type: purchase.payment_type,
        }));
      },

      handleAction() {
        this.loading = true;
        axios.post(`/inventory/purchase/reverse/${this.currentID}`)
          .then(response => {
            this.loading = false;
            if (response.status === 200) { this.toast.success(response.data.message); this.fetchData(); }
          })
          .catch(error => { this.loading = false; this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },

      handleApprove(id) {
        this.actionLoading = id;
        axios.post(`/inventory/purchase/approve/${id}`)
          .then(response => {
            this.actionLoading = null;
            this.toast.success(response.data.message);
            this.fetchData();
          })
          .catch(error => {
            this.actionLoading = null;
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
          });
      },

      handleReject(id) {
        this.actionLoading = id;
        axios.post(`/inventory/purchase/reject/${id}`)
          .then(response => {
            this.actionLoading = null;
            this.toast.success(response.data.message);
            this.fetchData();
          })
          .catch(error => {
            this.actionLoading = null;
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
          });
      },

      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },

      fetchData(url = '/inventory/purchase') {
        this.loading = true; this.crudLoading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.print_content = [];
            this.loading = false; this.crudLoading = false;
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.loading = false; this.crudLoading = false;
            this.toast.error(error?.response?.data?.message ?? 'Something went wrong');
          });
      },

      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>

  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

  .date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; }
  .date-field  { position:relative; display:flex; align-items:center; }
  .date-icon   { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
  :deep(.date-picker) { border:none !important; background:transparent !important; box-shadow:none !important; font-size:13px; width:130px; padding-left:22px !important; }
  :deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; }
  .date-sep    { color:#aab8cc; font-size:13px; }

  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }

  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }

  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .mt-2        { margin-top:8px; }
  .mt-3        { margin-top:12px; }

  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:100px; text-align:center; }
  .text-muted-cell { color:#4a5e78; font-size:13px; }

  .batch-code { font-family:monospace; font-size:13px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; }
  .date-primary   { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 2px; }
  .date-secondary { font-size:12px; color:#7a90b0; margin:0; }
  .store-tag  { display:inline-block; background:#f4f7fb; color:#4a5e78; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:12.5px; }
  .amount-value { font-weight:700; color:#004AAD; font-size:14px; }

  .user-cell   { display:flex; align-items:center; gap:8px; }
  .user-avatar { width:28px; height:28px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; text-transform:uppercase; }
  .user-avatar--sm { width:24px; height:24px; font-size:10px; }

  .payment-badge    { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .payment--cash    { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .payment--card    { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .payment--transfer{ background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .payment--part,
  .payment--split   { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .payment--credit  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }

  .status-chip       { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .status--pending   { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--approved  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--rejected  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }

  .action-btns  { display:flex; align-items:center; justify-content:center; gap:5px; }
  .action-btn   { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; }
  .action-btn--view    { color:#0d9488; }
  .action-btn--view:hover:not(.disabled)    { background:#f0fdfb; border-color:#a7f3d0; }
  .action-btn--approve { color:#16a34a; border-color:#bbf7d0; }
  .action-btn--approve:hover:not(:disabled) { background:#f0fdf4; }
  .action-btn--approve:disabled { color:#ccc; cursor:not-allowed; border-color:#e4eaf2; }
  .action-btn--reject  { color:#e03535; border-color:#fac5c5; }
  .action-btn--reject:hover:not(:disabled)  { background:#fff0f0; }
  .action-btn--reject:disabled  { color:#ccc; cursor:not-allowed; border-color:#e4eaf2; }
  .action-btn--reverse { color:#e08c00; }
  .action-btn--reverse:hover:not(:disabled) { background:#fff7ed; border-color:#fed7aa; }
  .action-btn--reverse:disabled { color:#ccc; cursor:not-allowed; }
  .action-btn.disabled { color:#ccc; pointer-events:none; }

  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }

  @media(max-width:991px) { .date-range { flex-wrap:wrap; } }
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .date-range { width:100%; }
  }

  /* -- Column picker modal ------------------------------------------------- */
  .col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
  .col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
  .col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
  .col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
  .col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
  .col-picker-close:hover { color:#1a2b45; }
  .col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
  .col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
  .col-picker-link:hover { text-decoration:underline; }
  .col-picker-sep      { color:#c5d3e0; }
  .col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
  .col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
  .col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
  .col-picker-item:hover { background:#f4f7fb; }
  .col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
  .col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
  .fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
  .fade-enter, .fade-leave-to { opacity:0; }
  </style>
